/**
 * useFingeringEditorCore (issue #22 Phase A)
 *
 * useFingeringEditor の共通業務ロジックを抽出した hook。
 * Mobile / Desktop hook の上位ノードから呼ばれ、 プラットフォーム非依存の
 * state / refs / handlers / computed / lifecycle を提供する。
 *
 * spec: docs/superpowers/specs/2026-05-24-fingering-editor-3layer-split-design.md §3.2.1
 * plan: docs/superpowers/plans/2026-05-24-fingering-editor-3layer-split.md Phase A
 *
 * NOTE: 本ファイルは Phase A 時点では未使用 (誰からも呼ばれない)。
 * Phase D で wrapper の compose 元になる。
 */
import { useState, useCallback, useRef, useEffect, useMemo, MutableRefObject, RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  FingeringData,
  BadgeStatus,
  DragState,
  EditorMode,
  RepeatEditConfirm,
  RepeatEditUpdates,
} from '../types/fingeringEditor';
import {
  beatsToBadges,
  badgesToBeats,
  resortChordNoteIndexes,
  NOTE_Y_SPACING,
} from '../utils/fingeringTransform';
import {
  buildRepeatIndex,
  highlightedBadgeIdsFor,
  propagateEdit,
  propagateDelete,
} from '../utils/repeatPropagation';
import { loadFingeringResult, saveFingeringResult } from '../utils/fingeringStorage';
import { hasValidDriveToken, getCombinedDriveToken } from '../utils/driveTokenStore';
import { getAutoSaveEnabled } from '../utils/autoSaveSettings';
import { needsDriveReauth } from '../utils/regenerateAuthGate';
import { buildBaseName, buildResultFileName, partNumFromPart, resolveOriginalTitle } from '../utils/fileNaming';
import { isDragTarget } from '../utils/dragTarget';
import type { FingeringNavigateState, PendingAction, FingeringPart, PdfColorMode, FingeringMode } from '../types/fingering';
import { getPdfColorMode, setPdfColorMode as savePdfColorMode } from '../utils/pdfColorMode';
import { usePdfBackground } from './usePdfBackground';
import { clampPan, Rect } from '../utils/panClamp';
import { computeMeasureLines, MeasureLine } from '../utils/measureLines';
import { zoomOutScalePan, snapPivotToCursor, isRectLaidOut } from '../utils/zoomPivot';
import { clampBadgeDelta } from '../utils/badgeClamp';
import { badgesInRect } from '../utils/badgesInRect';
import { isTouchDevice } from '../utils/deviceDetection';
import { useSyncedRef } from './useSyncedRef';
import { useRangeSelection, SelectRectState } from './useRangeSelection';
import { features } from '../config/features';
// TODO: REMOVE AFTER #5 DEBUG — issue #28 Phase 2-B 観測強化 (C-1 / C-3 / C-4 / C-5)
import { dragDebug, dragDebugThrottled } from '../pages/fingering/components/editor/dragDebug';
import { TOUCH_DRAG_OFFSET_Y } from '../constants/dragOffset';

// SVG 仮想座標サイズ (PDF 一枚相当)。 useFingeringEditor.ts と同値。
const SVG_WIDTH = 595;
const SVG_HEIGHT = 842;
// drag 昇格の移動量しきい値 (px)。 useFingeringEditor.ts の DRAG_THRESHOLD_PX と同値。
// 元 hook 側で named export 維持されているのでここでは再 declare のみ。
const DRAG_THRESHOLD_PX = 5;
const DRAG_THRESHOLD_SQ = DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX;

// issue #165: Drive 連携が構成済か (Google Client ID の有無)。 auto-save の isConfigured と同型。
// env 由来の定数なのでモジュールスコープで 1 度だけ解決する。
const IS_DRIVE_CONFIGURED = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

/** 命名ソース (元タイトル) の解決材料。 stored 由来の値を保持する。 */
interface NamingSource {
  storedOriginalTitle?: string;
  storedFileName?: string;
  storedMode?: FingeringMode;
}

/**
 * 保存 (saveFingeringResult) に渡す元タイトル + 結果 PDF 名を解決する (issue #152)。
 * 元タイトルは stored.originalTitle → meta.title → stored.fileName の順で解決し、 旧データは
 * derive で正規化する (自己治癒)。 meta.title を直に base にすると再 OMR 経路で完成 PDF 名に
 * 汚染された値を食い `_運指済` が二重付与されるため、 必ず元タイトル起点で組む。
 *
 * 結果名は fileNaming の規則 (buildResultFileName) に揃え、 生 part (Guitar1) を排除する
 * (issue #91 round2 (#44))。 mode 欠落 (古い stored) は Q-R2-1 裁定どおり番号付き (= 'score' 相当)。
 * 通常保存 / 再 OMR の両経路で本関数を使い、 名前ぶれを防ぐ。
 */
function resolveNaming(
  data: FingeringData,
  src: NamingSource,
): { originalTitle: string | undefined; fileName: string } {
  const originalTitle = resolveOriginalTitle({
    originalTitle: src.storedOriginalTitle,
    metaTitle: data.meta.title,
    fileName: src.storedFileName,
  });
  // meta.part 欠落 (旧 stored) は 1st 相当に倒す。 buildBaseName は空文字を untitled に落とす。
  const partNum = partNumFromPart(data.meta.part) || 1;
  const mode: FingeringMode = src.storedMode ?? 'score';
  return {
    originalTitle: originalTitle || undefined,
    fileName: buildResultFileName(buildBaseName(originalTitle), partNum, mode, 'pdf'),
  };
}

// issue #166: 編集を伝播したときの保存トースト文言 (count>0 なら伝播件数を添える)。
// handleSave / confirmRepeatAll の両方から使うので単一定義に集約する。
function propagatedSaveMessage(count: number): string {
  return count > 0 ? `他 ${count} 箇所にも適用しました` : '保存しました';
}

function buildExportJson(badges: Badge[], fingeringData: FingeringData): string {
  return JSON.stringify({
    meta: fingeringData.meta,
    beats: badgesToBeats(badges, fingeringData.beats),
    // issue #166: 再ロード後も伝播が効くよう repeat_groups を保存に含める (運指値は不変)。
    ...(fingeringData.repeat_groups ? { repeat_groups: fingeringData.repeat_groups } : {}),
  });
}

export interface UseFingeringEditorCoreReturn {
  data: {
    badges: Badge[];
    setBadges: React.Dispatch<React.SetStateAction<Badge[]>>;
    fingeringData: FingeringData | null;
    setFingeringData: React.Dispatch<React.SetStateAction<FingeringData | null>>;
    originalPdfBlob: Blob | null;
    setOriginalPdfBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
    mxlBlob: Blob | null;
    setMxlBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
    badgesHistory: Badge[][];
    setBadgesHistory: React.Dispatch<React.SetStateAction<Badge[][]>>;
    pageBadges: Badge[];
    chordGroups: Map<number, Badge[]>;
    measureLines: MeasureLine[];
    stats: Record<BadgeStatus, number>;
    selected: Badge | null;
    canUndo: boolean;
    // issue #166: 選択/編集した瞬間だけ点る対応バッジ id 集合 (常時は空)。
    highlightedBadgeIds: ReadonlySet<number>;
  };
  view: {
    zoom: number;
    setZoom: React.Dispatch<React.SetStateAction<number>>;
    pan: { x: number; y: number };
    setPan: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
    zoomOrigin: { x: number; y: number };
    setZoomOrigin: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
    isMobile: boolean;
    setIsMobile: React.Dispatch<React.SetStateAction<boolean>>;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    pageCount: number;
    pdfDataUrl: string | null;
    toast: string | null;
    setToast: React.Dispatch<React.SetStateAction<string | null>>;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  };
  mode: {
    editorMode: EditorMode;
    setEditorMode: React.Dispatch<React.SetStateAction<EditorMode>>;
    addMode: boolean;
    setAddMode: React.Dispatch<React.SetStateAction<boolean>>;
    // issue #91 round2 (#44): EditorHeader の part 表示出し分け用。 stored.mode 由来 (欠落時 undefined)。
    storedMode: FingeringMode | undefined;
    // issue #16 Phase 3: selectedId → selectedIds (ReadonlySet) 移行。
    // selectedId / setSelectedId は後方互換 derived (Phase 8 で page 更新後に削除予定)。
    selectedIds: ReadonlySet<number>;
    primarySelectedId: number | null;
    selectedCount: number;
    isMultiSelect: boolean;
    // issue #16 v3 Phase A: selectMode は派生値 (!isMobile || editorMode==='move')。
    // 範囲選択が有効なモードかを表す。 setSelectMode は撤去 (Mobile は editorMode 切替経由、 Desktop は常時 true)。
    selectMode: boolean;
    selectRectVisible: SelectRectState | null;
    setSelectRectVisible: (r: SelectRectState | null) => void;
    // 後方互換 (page 側で `mode.selectedId` を destructure している間のみ残置)
    selectedId: number | null;
    setSelectedId: (id: number | null) => void;
    panelOpen: boolean;
    setPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
  drag: {
    dragBadgeId: number | null;
    setDragBadgeId: React.Dispatch<React.SetStateAction<number | null>>;
    dragDelta: { dx: number; dy: number } | null;
    setDragDelta: React.Dispatch<React.SetStateAction<{ dx: number; dy: number } | null>>;
  };
  edit: {
    chordPickerBeatIndex: number | null;
    setChordPickerBeatIndex: React.Dispatch<React.SetStateAction<number | null>>;
    chordPickerPos: { x: number; y: number };
    setChordPickerPos: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
    detectedBpm: number | undefined;
    setDetectedBpm: React.Dispatch<React.SetStateAction<number | undefined>>;
    currentBpm: number;
    setCurrentBpm: React.Dispatch<React.SetStateAction<number>>;
    tuningChangeOpen: boolean;
    setTuningChangeOpen: React.Dispatch<React.SetStateAction<boolean>>;
    pendingScordatura: string;
    setPendingScordatura: React.Dispatch<React.SetStateAction<string>>;
    // PDF 出力の色モード (issue #32)。 setter は localStorage にも persist する。
    pdfColorMode: PdfColorMode;
    setPdfColorMode: (mode: PdfColorMode) => void;
    // issue #166: 自動変更バッジの再編集で立つ確認モーダルの保留 state (null=非表示)。
    repeatEditConfirm: RepeatEditConfirm | null;
  };
  regenerate: {
    pendingGen: PendingAction | null;
    setPendingGen: React.Dispatch<React.SetStateAction<PendingAction | null>>;
    isGenerating: boolean;
    isGeneratingAudio: boolean;
    // issue #165: 投入前の Drive 再ログインゲート。
    reauthModalOpen: boolean;
    handleReauthConfirm: () => Promise<void>;
    handleReauthCancel: () => void;
  };
  refs: {
    svgRef: RefObject<SVGSVGElement | null>;
    svgWrapperRef: RefObject<HTMLDivElement | null>;
    scrollContainerRef: RefObject<HTMLDivElement | null>;
    dragRef: MutableRefObject<DragState | null>;
    nextIdRef: MutableRefObject<number>;
    nextBeatIndexRef: MutableRefObject<number>;
    clickSuppressRef: MutableRefObject<boolean>;
    zoomRef: MutableRefObject<number>;
    panRef: MutableRefObject<{ x: number; y: number }>;
    zoomOriginRef: MutableRefObject<{ x: number; y: number }>;
    editorModeRef: MutableRefObject<EditorMode>;
    addModeRef: MutableRefObject<boolean>;
  };
  handlers: {
    showToast: (msg: string) => void;
    pushHistory: () => void;
    handleUndo: () => void;
    resetZoomToInitial: () => void;
    applyPanClamp: (nextPan: { x: number; y: number }) => { x: number; y: number };
    applyZoomWithPivot: (cx: number, cy: number, nextZoom: number) => void;
    clientToSvg: (clientX: number, clientY: number) => { x: number; y: number } | null;
    dismissPanel: () => void;
    discardEmptyAddedBadge: (exceptId?: number) => void;
    handlePageChange: (page: number) => void;
    handleSvgClick: (e: React.MouseEvent<SVGSVGElement>) => void;
    handleBadgeSelect: (id: number, screenX?: number, screenY?: number) => void;
    finalizeDrag: (d: DragState, opts?: { applyTouchOffset?: boolean }) => void;
    addNoteToBeat: (beatIndex: number) => void;
    handleChordNoteSelect: (badgeId: number) => void;
    handleChordAddNote: () => void;
    handleChordPickerClose: () => void;
    handleSave: (
      id: number,
      updates: RepeatEditUpdates,
      options?: { keepPanelOpen?: boolean },
    ) => void;
    // issue #166: 確認モーダルの分岐ハンドラ (個別 / 全体)。
    confirmRepeatIndividual: () => void;
    confirmRepeatAll: () => void;
    handleDelete: (id: number) => void;
    // issue #16 Phase 3: 範囲削除 + 選択 API
    handleDeleteMany: (ids: number[]) => void;
    select: (id: number, opts?: { additive?: boolean }) => void;
    toggle: (id: number) => void;
    selectRange: (ids: number[], opts?: { additive?: boolean }) => void;
    clearSelection: () => void;
    // issue #16 v3 Phase E+F (DRY): 範囲選択 drag finalize の共通ヘルパー (Mobile/Desktop 共用)。
    commitRangeSelectFromVisible: (opts?: { additive?: boolean }) => boolean;
    handleSaveAndRegenerateAll: () => Promise<void> | void;
    handleGeneratePdfOnly: () => Promise<void> | void;
    handleGenerateAudioOnly: () => Promise<void> | void;
    handleTuningChangeRegenerate: (scordatura: string) => void;
    handleConfirmDiscard: () => Promise<void>;
    handleCancelDiscard: () => void;
    handleToggleAddMode: () => void;
    handleToolbarZoom: (newZoom: number) => void;
    // 入口分離 helper (spec §4.1)。 Mobile / Desktop の handleBadgeDragStart / handlePointerMove
    // から呼び出される共通部分。 long-press / panDrag 等のプラットフォーム固有処理は呼出し側で wrap する。
    tryStartDrag: (id: number, e: React.PointerEvent) => boolean;
    tryAdvanceDrag: (e: React.PointerEvent<SVGSVGElement>) => boolean;
  };
}

export function useFingeringEditorCore(): UseFingeringEditorCoreReturn {
  const navigate = useNavigate();

  // --- data state ---
  const [badges, setBadges] = useState<Badge[]>([]);
  const [fingeringData, setFingeringData] = useState<FingeringData | null>(null);
  const [originalPdfBlob, setOriginalPdfBlob] = useState<Blob | null>(null);
  const [mxlBlob, setMxlBlob] = useState<Blob | null>(null);
  const [badgesHistory, setBadgesHistory] = useState<Badge[][]>([]);
  // issue #81 追補❷: stored.mode を保持し、再生成の中継保存 / navigate state へ搬送する
  // (FingeringMeta に mode は無いため stored 経由でしか引き継げない)。古い stored は undefined。
  const [storedMode, setStoredMode] = useState<FingeringMode | undefined>(undefined);
  // issue #152: 命名ソース (元タイトル)。 stored.originalTitle が正、 欠落する旧データは
  // meta.title / stored.fileName から derive で救済する (resolveOriginalTitle)。
  const [storedOriginalTitle, setStoredOriginalTitle] = useState<string | undefined>(undefined);
  const [storedFileName, setStoredFileName] = useState<string | undefined>(undefined);

  // --- mode state ---
  // issue #16 Phase 3: selectedId → selectedIds (ReadonlySet) 移行
  // useRangeSelection は最新 badges を ref 経由で参照するので、ここでの compose 順序は OK。
  const selection = useRangeSelection(badges);
  const {
    selectedIds,
    selectRectVisible,
    setSelectRectVisible,
    select,
    toggle,
    selectRange,
    clear: clearSelection,
  } = selection;
  // spec §4.1.2 通りの互換 derived (size===1 のときのみ非 null、 既存 selectedId 参照箇所を透過置換可能)。
  // useRangeSelection 内部の primarySelectedId は「最後に select した起点」を覚える別意味の state
  // なので、 Core hook では spec の定義を優先する。
  const primarySelectedId = selectedIds.size === 1 ? Array.from(selectedIds)[0] : null;
  const selectedCount = selectedIds.size;
  const isMultiSelect = selectedIds.size >= 2;
  const [addMode, setAddMode] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  // editorMode は spec §4.5 方針 B により Core 所有。
  // Desktop では常に 'edit' 固定 (Mobile から setEditorMode が呼ばれる)。
  const [editorMode, setEditorMode] = useState<EditorMode>('edit');

  // 後方互換 setter: 旧 setSelectedId(id) / setSelectedId(null) を新 API に橋渡し。
  // Phase 8 で page 側を primarySelectedId / handlers.select に置換後に削除予定。
  const setSelectedId = useCallback((id: number | null) => {
    if (id === null) {
      clearSelection();
    } else {
      select(id);
    }
  }, [clearSelection, select]);

  // --- view state ---
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [isMobile, setIsMobile] = useState(false);
  // issue #16 v3 Phase A: selectMode を editorMode + isMobile から派生で計算 (state を持たない)。
  //   Desktop (isMobile=false): 常に true (Q-3 確定: Desktop はモードレス現状維持、 badge=移動/空白=範囲選択 を常時両対応)
  //   Mobile + editorMode='move': true (空白起点=範囲選択 / badge起点=個別移動)
  //   Mobile + editorMode='edit': false (範囲選択無効・運指 drag 不可)
  const selectMode = useMemo(
    () => !isMobile || editorMode === 'move',
    [isMobile, editorMode],
  );
  const [currentPage, setCurrentPage] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // --- drag state ---
  const [dragBadgeId, setDragBadgeId] = useState<number | null>(null);
  const [dragDelta, setDragDelta] = useState<{ dx: number; dy: number } | null>(null);

  // --- edit / chord picker / bpm / tuning change ---
  const [chordPickerBeatIndex, setChordPickerBeatIndex] = useState<number | null>(null);
  const [chordPickerPos, setChordPickerPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [detectedBpm, setDetectedBpm] = useState<number | undefined>();
  const [currentBpm, setCurrentBpm] = useState<number>(120);
  const [tuningChangeOpen, setTuningChangeOpen] = useState(false);
  const [pendingScordatura, setPendingScordatura] = useState<string>('');

  // --- 繰り返し伝播 (issue #166) ---
  // 自動変更 (propagated) バッジの再編集で「個別 or 全体」を確認する保留 state。
  const [repeatEditConfirm, setRepeatEditConfirm] = useState<RepeatEditConfirm | null>(null);

  // --- pdf color mode (issue #32) ---
  // localStorage 既存値で lazy init。 setter は state + persist。
  const [pdfColorMode, setPdfColorModeState] = useState<PdfColorMode>(() => getPdfColorMode());
  const setPdfColorMode = useCallback((mode: PdfColorMode) => {
    setPdfColorModeState(mode);
    savePdfColorMode(mode);
  }, []);

  // --- regenerate queue ---
  const [pendingGen, setPendingGen] = useState<PendingAction | null>(null);
  const isGenerating = pendingGen === 'pdf' || pendingGen === 'both';
  const isGeneratingAudio = pendingGen === 'audio' || pendingGen === 'both';

  // --- Drive 再ログインゲート (issue #165) ---
  // gate 成立時は投入 (navigate) を止めてモーダルを出し、 対象の navigate state を ref に stash する。
  // 再ログイン成功後にこの state で navigate し直す。
  const [reauthModalOpen, setReauthModalOpen] = useState(false);
  const pendingNavStateRef = useRef<FingeringNavigateState | null>(null);

  // --- refs (共通のみ。 platform 固有 ref は Mobile / Desktop hook で保持) ---
  const svgRef = useRef<SVGSVGElement>(null);
  const svgWrapperRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const nextIdRef = useRef(10000);
  const nextBeatIndexRef = useRef(0);
  const clickSuppressRef = useRef(false);

  const zoomRef = useSyncedRef(zoom);
  const panRef = useSyncedRef(pan);
  const zoomOriginRef = useSyncedRef(zoomOrigin);
  const editorModeRef = useSyncedRef(editorMode);
  const addModeRef = useSyncedRef(addMode);

  // --- PDF 背景画像 ---
  const { imageUrl: pdfDataUrl, pageCount } = usePdfBackground(originalPdfBlob, currentPage + 1, 2);

  // --- computed (c1-c6) ---
  const pageBadges = useMemo(
    () => badges.filter((b) => b.page === currentPage),
    [badges, currentPage],
  );

  const chordGroups = useMemo(() => {
    const groups = new Map<number, Badge[]>();
    for (const b of pageBadges) {
      if (b.beatIndex !== null) {
        const group = groups.get(b.beatIndex) ?? [];
        group.push(b);
        groups.set(b.beatIndex, group);
      }
    }
    // group はこの memo 内で新規生成した所有配列なので in-place sort でよい (badges 自体は不変)。
    groups.forEach((group) => {
      group.sort((a, b) => (a.noteIndex ?? 0) - (b.noteIndex ?? 0));
    });
    return groups;
  }, [pageBadges]);

  const measureLines = useMemo(() => {
    if (!fingeringData) return [];
    return computeMeasureLines(fingeringData.beats, currentPage);
  }, [fingeringData, currentPage]);

  const stats = useMemo<Record<BadgeStatus, number>>(() => {
    const s: Record<BadgeStatus, number> = { auto: 0, edited: 0, added: 0 };
    for (const b of badges) s[b.status]++;
    return s;
  }, [badges]);

  const selected = useMemo(
    () => badges.find((b) => b.id === primarySelectedId) ?? null,
    [badges, primarySelectedId],
  );

  const canUndo = badgesHistory.length > 0;

  // --- 繰り返し伝播の索引 (issue #166) ---
  // buildRepeatIndex が undefined を許容するので中間 memo は挟まず直接渡す。
  const repeatIndex = useMemo(() => buildRepeatIndex(fingeringData?.repeat_groups), [fingeringData]);

  // 選択/編集した瞬間だけ、 同グループの対応バッジ (同 offset・同 noteIndex) を一時ハイライトする。
  // 純ロジックは repeatPropagation に集約 (常時マーキングはしない = issue #120 方針維持)。
  const highlightedBadgeIds = useMemo(
    () => highlightedBadgeIdsFor(badges, selectedIds, repeatIndex),
    [selectedIds, badges, repeatIndex],
  );

  // ============================================================
  // useEffect (lifecycle / listeners)
  // ============================================================
  // e1: 初期データロード / e2: isMobile resize / e3: page 変更時 zoom/pan reset
  // e4 (long-press cleanup) は Phase B で Mobile hook が所有

  // 初期データロード
  useEffect(() => {
    loadFingeringResult().then((stored) => {
      if (stored?.fingeringJson) {
        try {
          const data: FingeringData = JSON.parse(stored.fingeringJson);
          setFingeringData(data);
          const b = beatsToBadges(data);
          setBadges(b);
          nextIdRef.current = b.length + 1;
          nextBeatIndexRef.current = data.beats.length;
        } catch {
          // malformed JSON
        }
      }
      if (stored?.originalPdfBlob) setOriginalPdfBlob(stored.originalPdfBlob);
      if (stored?.mxlBlob) setMxlBlob(stored.mxlBlob);
      if (stored?.mode) setStoredMode(stored.mode);
      // issue #152: 命名ソースの解決材料 (元タイトル / 完成 PDF 名) を保持する。
      if (stored?.originalTitle) setStoredOriginalTitle(stored.originalTitle);
      if (stored?.fileName) setStoredFileName(stored.fileName);
      if (stored?.detectedBpm) {
        setDetectedBpm(stored.detectedBpm);
        setCurrentBpm(stored.detectedBpm);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // isMobile resize listener
  // issue #125: タブレット (iPad 等) は幅が広く width 判定だけでは Desktop 扱いになるため、
  //   タッチデバイス (isTouchDevice) なら幅に関わらず Mobile UI を出す。
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768 || isTouchDevice());
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // issue #16 v3 Phase A: 旧 selectModeInitializedRef useEffect は撤去
  // (selectMode は editorMode + isMobile の派生値、 上の useMemo を参照)。

  // ページ遷移時 zoom/pan reset
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setZoomOrigin({ x: 50, y: 50 });
  }, [currentPage]);

  // ============================================================
  // handlers
  // ============================================================

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  }, []);

  const pushHistory = useCallback(() => {
    setBadgesHistory((prev) => [...prev.slice(-19), badges]);
  }, [badges]);

  const handleUndo = useCallback(() => {
    setBadgesHistory((prev) => {
      if (prev.length === 0) return prev;
      const newHistory = [...prev];
      const lastState = newHistory.pop()!;
      setBadges(lastState);
      return newHistory;
    });
  }, []);

  const resetZoomToInitial = useCallback(() => {
    const resetPan = { x: 0, y: 0 };
    const resetOrigin = { x: 50, y: 50 };
    zoomRef.current = 1;
    zoomOriginRef.current = resetOrigin;
    panRef.current = resetPan;
    setZoomOrigin(resetOrigin);
    setPan(resetPan);
    setZoom(1);
  }, [zoomRef, panRef, zoomOriginRef]);

  const applyPanClamp = useCallback((nextPan: { x: number; y: number }): { x: number; y: number } => {
    const container = scrollContainerRef.current;
    const wrapper = svgWrapperRef.current;
    if (!container || !wrapper) return nextPan;
    const containerRect: Rect = container.getBoundingClientRect();
    const wrapperRect: Rect = wrapper.getBoundingClientRect();
    if (!isRectLaidOut(containerRect) && !isRectLaidOut(wrapperRect)) return nextPan;
    const clamped = clampPan(panRef.current, nextPan, containerRect, wrapperRect);
    panRef.current = clamped;
    return clamped;
  }, [panRef]);

  const applyZoomWithPivot = useCallback((cx: number, cy: number, nextZoom: number) => {
    if (nextZoom <= 1) {
      resetZoomToInitial();
      return;
    }
    const wrapper = svgWrapperRef.current;
    if (!wrapper) {
      zoomRef.current = nextZoom;
      setZoom(nextZoom);
      return;
    }
    const rect = wrapper.getBoundingClientRect();
    if (!isRectLaidOut(rect)) {
      zoomRef.current = nextZoom;
      setZoom(nextZoom);
      return;
    }
    const currentZoom = zoomRef.current;
    let nextPivot: { x: number; y: number };
    let nextPan: { x: number; y: number };
    if (nextZoom > currentZoom) {
      const { pivot, pan: snappedPan } = snapPivotToCursor(
        rect, cx, cy, zoomOriginRef.current, currentZoom, panRef.current,
      );
      nextPivot = pivot;
      nextPan = snappedPan;
    } else {
      nextPivot = zoomOriginRef.current;
      nextPan = zoomOutScalePan(currentZoom, nextZoom, panRef.current);
    }
    const prevPan = panRef.current;
    const clampedPan = applyPanClamp(nextPan);
    const panChanged = Math.abs(clampedPan.x - prevPan.x) > 1e-6
      || Math.abs(clampedPan.y - prevPan.y) > 1e-6;
    zoomRef.current = nextZoom;
    zoomOriginRef.current = nextPivot;
    panRef.current = clampedPan;
    setZoomOrigin(nextPivot);
    if (panChanged) setPan(clampedPan);
    setZoom(nextZoom);
  }, [applyPanClamp, resetZoomToInitial, panRef, zoomOriginRef, zoomRef]);

  // I-A issue #12: iOS Safari の getScreenCTM が CSS scale を無視するため rect 比例方式に統一
  const clientToSvg = useCallback((clientX: number, clientY: number): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    // TODO: REMOVE AFTER #5 DEBUG — C-5 (issue #28 Phase 2-B): rect.top 連続観察 (5 回ごとに throttle)
    dragDebugThrottled('rect', 5, `rect.top=${Math.round(rect.top)} (clientToSvg)`);
    return {
      x: ((clientX - rect.left) / rect.width) * SVG_WIDTH,
      y: ((clientY - rect.top) / rect.height) * SVG_HEIGHT,
    };
  }, []);

  // issue #51: 選択切替・モード切替・ページ切替の前段で「未入力のまま放置された空 added badge」を掃除する。
  // exceptId に渡した id 自身は除去しない (同一 badge 再選択で自分を消さないため)。
  const discardEmptyAddedBadge = useCallback((exceptId?: number) => {
    if (primarySelectedId === null || primarySelectedId === exceptId) return;
    const badge = badges.find((b) => b.id === primarySelectedId);
    if (badge && badge.status === 'added' && (badge.string == null || badge.fret == null)) {
      setBadges((prev) => prev.filter((b) => b.id !== primarySelectedId));
    }
  }, [primarySelectedId, badges]);

  const dismissPanel = useCallback(() => {
    discardEmptyAddedBadge();
    clearSelection();
    setPanelOpen(false);
  }, [discardEmptyAddedBadge, clearSelection]);

  const handlePageChange = useCallback((page: number) => {
    discardEmptyAddedBadge();
    setCurrentPage(page);
  }, [discardEmptyAddedBadge]);

  const handleSvgClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (dragRef.current) return;
    if (clickSuppressRef.current) { clickSuppressRef.current = false; return; }
    setChordPickerBeatIndex(null);
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * SVG_WIDTH;
    const y = ((e.clientY - rect.top) / rect.height) * SVG_HEIGHT;
    if (addMode) {
      pushHistory();
      const id = nextIdRef.current++;
      const beatIndex = nextBeatIndexRef.current++;
      setBadges((prev) => [...prev, {
        id, x, y, note: '', string: null, fret: null, status: 'added',
        beatIndex, noteIndex: 0, page: currentPage,
      }]);
      select(id);
      setAddMode(false);
      setPanelOpen(true);
      showToast('バッジを配置しました');
      // issue #163: スマホは配置直後に move モードへ切り替え、置いたバッジをすぐ微調整できるようにする。
      // PC はモードレス運用のため edit のまま (setEditorMode を通さない)。
      if (isMobile) setEditorMode('move');
    } else {
      dismissPanel();
    }
  }, [addMode, isMobile, showToast, currentPage, pushHistory, dismissPanel, select]);

  const handleBadgeSelect = useCallback((id: number, screenX?: number, screenY?: number) => {
    discardEmptyAddedBadge(id);
    const badge = badges.find((b) => b.id === id);
    if (!badge) return;
    if (badge.beatIndex !== null) {
      const group = chordGroups.get(badge.beatIndex);
      if (group && group.length > 1) {
        setChordPickerBeatIndex(badge.beatIndex);
        setChordPickerPos({ x: screenX ?? 200, y: screenY ?? 200 });
        return;
      }
    }
    if (!addMode) {
      select(id);
      setPanelOpen(true);
    }
  }, [addMode, badges, chordGroups, select, discardEmptyAddedBadge]);

  // spec §4.1: 入口分離 helper
  // 元 handleBadgeDragStart (useFingeringEditor.ts L618-645) のうち
  // 「clickSuppressRef リセット + clientToSvg + dragRef 初期化」を Core で受け持つ。
  // long-press タイマー起動は Mobile hook が wrap して呼び足す (Phase B)。
  // 注: 元コードでは setPointerCapture は pointerdown 段階では呼ばず、 dragging 昇格時 (handlePointerMove) で呼ぶ。
  //     issue #5 F-1 revert で「pointerdown で <g> への setPointerCapture を呼ばない」が確定挙動。
  const tryStartDrag = useCallback((_id: number, e: React.PointerEvent): boolean => {
    clickSuppressRef.current = false;
    const svgPt = clientToSvg(e.clientX, e.clientY);
    if (!svgPt) return false;
    const badge = badges.find((b) => b.id === _id);
    if (!badge) return false;
    const state: DragState = {
      badgeId: _id,
      beatIndex: badge.beatIndex,
      startX: badge.x,
      startY: badge.y,
      currentX: badge.x,
      currentY: badge.y,
      phase: 'pending',
      startClientX: e.clientX,
      startClientY: e.clientY,
      startSvgX: svgPt.x,
      startSvgY: svgPt.y,
    };
    dragRef.current = state;
    // TODO: REMOVE AFTER #5 DEBUG — C-1 (issue #28 Phase 2-B): drag 開始時の startSvgY 観測
    dragDebug(`start svgY=${Math.round(svgPt.y)} badge.y=${Math.round(badge.y)} clientY=${Math.round(e.clientY)}`);
    return true;
  }, [badges, clientToSvg]);

  // spec §4.1: 入口分離 helper
  // 元 handlePointerMove のうち「pending → dragging 昇格 (threshold 判定) + dragDelta 更新」を Core で受け持つ。
  // pinch / singleFingerPan / panDrag / long-press miss などのプラットフォーム固有判定は呼出し側で。
  // 戻り値: dragging 状態を進めた (= 呼出し側で追加処理しなくて良い) → true
  const tryAdvanceDrag = useCallback((e: React.PointerEvent<SVGSVGElement>): boolean => {
    const d = dragRef.current;
    if (!d) return false;
    if (d.phase === 'pending') {
      const dxClient = e.clientX - d.startClientX;
      const dyClient = e.clientY - d.startClientY;
      const distSq = dxClient * dxClient + dyClient * dyClient;
      if (distSq < DRAG_THRESHOLD_SQ) return false;
      d.phase = 'dragging';
      setDragBadgeId(d.badgeId);
      try { svgRef.current?.setPointerCapture?.(e.pointerId); } catch { /* 同上 */ }
    }
    const svgPt = clientToSvg(e.clientX, e.clientY);
    if (!svgPt) return true;
    d.currentX = svgPt.x;
    d.currentY = svgPt.y;
    setDragDelta({ dx: svgPt.x - d.startSvgX, dy: svgPt.y - d.startSvgY });
    return true;
  }, [clientToSvg]);

  const finalizeDrag = useCallback((d: DragState, opts?: { applyTouchOffset?: boolean }) => {
    const rawDelta = dragDelta;
    // TODO: REMOVE AFTER #5 DEBUG — C-3 (issue #28 Phase 2-B): finalize 時の dragDelta snapshot
    dragDebug(`finalize raw_dy=${rawDelta ? Math.round(rawDelta.dy) : 'null'} (state) applyTouchOffset=${opts?.applyTouchOffset ?? false}`);
    if (rawDelta) {
      // P-2 (issue #28 v3): Mobile (touch) drag では「視覚位置にドロップ」 のため
      // dragDelta.dy から TOUCH_DRAG_OFFSET_Y を差し引く。 PC mouse drag (default) は無補正。
      // 補正は clamp 前に行う (rawDelta レベル) ことで上端での挙動も自然に保たれる。
      const adjustedDelta = opts?.applyTouchOffset
        ? { dx: rawDelta.dx, dy: rawDelta.dy - TOUCH_DRAG_OFFSET_Y }
        : rawDelta;
      // issue #16 v3 Phase E: 選択群の一括移動 (Spec #3)。
      //   掴んだ badge が選択群の一員 かつ 複数選択中なら、 選択群全体を同じ delta で動かす。
      //   そうでなければ従来どおり「掴んだ badge + 同 beat (和音グループ)」を動かす。
      //   issue #118: preview(Page) と同一の SSOT ヘルパー isDragTarget に統一し条件ドリフトを防ぐ。
      const isTarget = (b: Badge): boolean =>
        isDragTarget(b, d.badgeId, d.beatIndex, selectedIds);
      const targets = badges.filter(isTarget);
      const delta = clampBadgeDelta(
        targets.map((b) => ({ x: b.x, y: b.y })),
        adjustedDelta,
        { width: SVG_WIDTH, height: SVG_HEIGHT },
      );
      const moved = Math.abs(delta.dx) > 1 || Math.abs(delta.dy) > 1;
      if (moved) {
        pushHistory();
        setBadges((prev) => prev.map((b) => {
          if (!isTarget(b)) return b;
          const next: Badge = { ...b, x: b.x + delta.dx, y: b.y + delta.dy };
          if (b.status === 'auto') next.status = 'edited';
          // TODO: REMOVE AFTER #5 DEBUG — C-4 (issue #28 Phase 2-B): commit 後の badge.y 観測
          if (b.id === d.badgeId) {
            dragDebug(`commit badge.y=${Math.round(next.y)} (was=${Math.round(b.y)})`);
          }
          return next;
        }));
        showToast('位置を調整しました');
      }
    }
    setDragDelta(null);
    dragRef.current = null;
    setDragBadgeId(null);
    // issue #149: PC (mouse) 版で複数選択群を移動した直後、 掴んだ位置で発火する合成 click
    //   (handleSvgClick) が選択を解除してしまうのを防ぐ。 finalizeDrag は mobile/desktop 両経路が
    //   通る唯一の合流点なので、 ここで clickSuppress を立てるのが正しい (旧 fix を desktop 経路に
    //   置いても mobile が先に drag を消費するため dead path だった)。 touch drop (applyTouchOffset)
    //   は合成 click を伴わないため除外する。
    if (isMultiSelect && !opts?.applyTouchOffset) {
      clickSuppressRef.current = true;
    }
  }, [showToast, pushHistory, dragDelta, badges, selectedIds, isMultiSelect]);

  const addNoteToBeat = useCallback((beatIndex: number) => {
    const group = chordGroups.get(beatIndex);
    if (!group || group.length === 0) return;
    pushHistory();
    const refBadge = group[0];
    const newId = nextIdRef.current++;
    const newY = refBadge.y - group.length * NOTE_Y_SPACING;
    setBadges((prev) => [...prev, {
      id: newId, x: refBadge.x, y: newY, note: '', string: null, fret: null,
      status: 'added', beatIndex, noteIndex: group.length, page: currentPage,
    }]);
    setChordPickerBeatIndex(null);
    select(newId);
    setPanelOpen(true);
    setAddMode(false);
    showToast('和音に音を追加しました');
  }, [chordGroups, currentPage, showToast, pushHistory, select]);

  const handleChordNoteSelect = useCallback((badgeId: number) => {
    setChordPickerBeatIndex(null);
    // issue #45 バグ C (案 C-2): ChordNotePicker からの単一音符選択は §5 C-2 (chord=beat) の例外。
    //   noExpand:true で peer 展開をバイパス → 単一 badge のみ選択 → selected (Core 派生) 非 null → EditorPanel 表示。
    //   matching/drag/削除/矩形選択は従来 select (expand あり) を維持。
    select(badgeId, { noExpand: true });
    setPanelOpen(true);
  }, [select]);

  const handleChordAddNote = useCallback(() => {
    if (chordPickerBeatIndex === null) return;
    addNoteToBeat(chordPickerBeatIndex);
  }, [chordPickerBeatIndex, addNoteToBeat]);

  const handleChordPickerClose = useCallback(() => {
    setChordPickerBeatIndex(null);
  }, []);

  // issue #166: source バッジへ updates を適用し、 グループ内なら対応位置へ伝播して反映する共通処理。
  // handleSave (通常編集) と confirmRepeatAll (全体適用) が共有。 sourceExtra は source バッジのみに
  // 上書きする追加フラグ (propagated / status 等)。 伝播で実際に変わった他出現数を返す。
  const applyEditWithPropagation = useCallback((
    targetId: number,
    updates: RepeatEditUpdates,
    sourceExtra?: Partial<Badge>,
  ): number => {
    const withSource = badges.map((b) => b.id === targetId ? { ...b, ...updates, ...sourceExtra } : b);
    const { badges: applied, count } = propagateEdit(withSource, targetId, updates, repeatIndex);
    setBadges(resortChordNoteIndexes(applied));
    return count;
  }, [badges, repeatIndex]);

  const handleSave = useCallback((
    id: number,
    updates: RepeatEditUpdates,
    options?: { keepPanelOpen?: boolean },
  ) => {
    const target = badges.find((b) => b.id === id);
    // issue #166: 自動変更 (伝播で変わった未確認) バッジの編集は、 一旦保留して確認モーダルを出す。
    // ユーザが「個別/全体」を選ぶまで値は確定しない。
    if (target && target.propagated && !target.manualOwned) {
      setRepeatEditConfirm({ badgeId: id, updates });
      return;
    }
    pushHistory();
    const count = applyEditWithPropagation(id, updates);
    if (!options?.keepPanelOpen) {
      clearSelection();
      setPanelOpen(false);
    }
    showToast(propagatedSaveMessage(count));
  }, [badges, showToast, pushHistory, clearSelection, applyEditWithPropagation]);

  // issue #166: 確認モーダルで「個別に編集」。 保留 updates を当該バッジのみに適用し、
  // manualOwned:true / propagated:false にして以降の伝播対象から外す (他へは伝播しない)。
  const confirmRepeatIndividual = useCallback(() => {
    const pending = repeatEditConfirm;
    if (!pending) return;
    pushHistory();
    setBadges((prev) => resortChordNoteIndexes(prev.map((b) => b.id === pending.badgeId
      ? { ...b, ...pending.updates, manualOwned: true, propagated: false, status: 'edited' as const }
      : b)));
    setRepeatEditConfirm(null);
    clearSelection();
    setPanelOpen(false);
    showToast('保存しました');
  }, [repeatEditConfirm, pushHistory, clearSelection, showToast]);

  // issue #166: 確認モーダルで「全体に適用」。 通常グループ編集として source を確定 (propagated:false) し、
  // 対応する全出現へ伝播する。
  const confirmRepeatAll = useCallback(() => {
    const pending = repeatEditConfirm;
    if (!pending) return;
    pushHistory();
    const count = applyEditWithPropagation(pending.badgeId, pending.updates, { propagated: false, status: 'edited' });
    setRepeatEditConfirm(null);
    clearSelection();
    setPanelOpen(false);
    showToast(propagatedSaveMessage(count));
  }, [repeatEditConfirm, pushHistory, clearSelection, showToast, applyEditWithPropagation]);

  // issue #16 v3 Phase E+F (DRY): 範囲選択 drag の finalize 共通ヘルパー。
  //   selectRectVisible (矩形オーバーレイ座標) を読み、 badgesInRect で hit を求めて selectRange する。
  //   Mobile (handleTouchEnd) / Desktop (handlePointerUp) の finalize 手続きを 1 箇所に集約 (二重実装解消)。
  //   戻り値: 実際に commit した (= 呼出し側で追加の tap 処理が不要) → true。
  const commitRangeSelectFromVisible = useCallback((opts?: { additive?: boolean }): boolean => {
    const visible = selectRectVisible;
    if (!visible) return false;
    const hits = badgesInRect(badges, {
      minX: Math.min(visible.startSvgX, visible.currentSvgX),
      minY: Math.min(visible.startSvgY, visible.currentSvgY),
      maxX: Math.max(visible.startSvgX, visible.currentSvgX),
      maxY: Math.max(visible.startSvgY, visible.currentSvgY),
      page: visible.page,
    });
    selectRange(hits.map((b) => b.id), { additive: opts?.additive });
    clickSuppressRef.current = true;
    setSelectRectVisible(null);
    return true;
  }, [selectRectVisible, badges, selectRange, setSelectRectVisible]);

  // issue #16 Phase 3: 範囲削除 (Cmd+Z で 1 ストロークで復元できるよう pushHistory は先頭 1 回)
  const handleDeleteMany = useCallback((ids: number[]) => {
    if (ids.length === 0) return;
    pushHistory();
    const idSet = new Set(ids);
    // issue #166: グループ内なら対応位置 (manualOwned 除外) も削除する。
    const { badges: afterPropagate, count } = propagateDelete(badges, ids, repeatIndex);
    setBadges(afterPropagate.filter((b) => !idSet.has(b.id)));
    clearSelection();
    setPanelOpen(false);
    const base = ids.length === 1 ? '削除しました' : `${ids.length}個削除しました`;
    showToast(count > 0 ? `${base}（他 ${count} 箇所も削除）` : base);
  }, [badges, pushHistory, clearSelection, showToast, repeatIndex]);

  const handleDelete = useCallback((id: number) => {
    handleDeleteMany([id]);
  }, [handleDeleteMany]);

  // issue #165: 投入 (navigate) の直前に Drive 再ログインの要否を判定する共通ゲート。
  // gate 成立 (自動保存ON + Drive構成済 + token失効) なら navigate を止め、 対象 state を stash して
  // ReauthRequiredModal を出す。 非 gate ならそのまま navigate する。
  // 保存 (saveFingeringResult) は呼出し側で既に済んでいるので、 再ログイン後は state で navigate するだけ。
  const submitOrGate = useCallback((state: FingeringNavigateState) => {
    if (needsDriveReauth({
      autoSaveEnabled: getAutoSaveEnabled(),
      isDriveConfigured: IS_DRIVE_CONFIGURED,
      hasValidToken: hasValidDriveToken(),
    })) {
      pendingNavStateRef.current = state;
      setReauthModalOpen(true);
      return;
    }
    navigate('/fingering', { state });
  }, [navigate]);

  // 「再ログインする」: consent 直行 (gesture 保持)。 成功したら stash した投入を navigate する。
  // 失敗時はモーダルを残置し toast を出す (ユーザは再試行 or キャンセルできる)。
  const handleReauthConfirm = useCallback(async () => {
    try {
      await getCombinedDriveToken({ interactive: true });
      const state = pendingNavStateRef.current;
      pendingNavStateRef.current = null;
      setReauthModalOpen(false);
      if (state) navigate('/fingering', { state });
    } catch (err) {
      showToast(err instanceof Error ? err.message : '再ログインに失敗しました');
    }
  }, [navigate, showToast]);

  const handleReauthCancel = useCallback(() => {
    pendingNavStateRef.current = null;
    setReauthModalOpen(false);
  }, []);

  // localStorage save + navigate(pendingAction) 共通 helper
  const navigateWithPending = useCallback(async (
    action: PendingAction,
    opts: { requireMxl?: boolean; includeBpm?: boolean; startFlags: () => void; endFlags: () => void },
  ): Promise<void> => {
    if (!fingeringData || !originalPdfBlob) { showToast('元PDFデータがありません'); return; }
    if (opts.requireMxl && !mxlBlob) { showToast('音源生成に必要な mxl データがありません'); return; }
    opts.startFlags();
    try {
      const { originalTitle, fileName } = resolveNaming(fingeringData, {
        storedOriginalTitle, storedFileName, storedMode,
      });
      const fingeringJson = buildExportJson(badges, fingeringData);
      await saveFingeringResult({
        pdfBlob: originalPdfBlob,
        fileName,
        originalTitle,
        fingeringJson,
        originalPdfBlob,
        mxlBlob: mxlBlob ?? undefined,
        detectedBpm,
        mode: storedMode,
      });
      const state: FingeringNavigateState = {
        pendingAction: action,
        requestedAt: Date.now(),
        colorMode: pdfColorMode,
        ...(opts.includeBpm ? { currentBpm } : {}),
        ...(storedMode ? { mode: storedMode } : {}),
      };
      submitOrGate(state);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      opts.endFlags();
    }
  }, [badges, fingeringData, originalPdfBlob, mxlBlob, detectedBpm, currentBpm, pdfColorMode, storedMode, storedOriginalTitle, storedFileName, showToast, submitOrGate]);

  const handleGenerateAudioOnly = useCallback(() => navigateWithPending('audio', {
    requireMxl: true,
    includeBpm: true,
    startFlags: () => setPendingGen('audio'),
    endFlags: () => setPendingGen(null),
  }), [navigateWithPending]);

  const handleGeneratePdfOnly = useCallback(() => navigateWithPending('pdf', {
    startFlags: () => setPendingGen('pdf'),
    endFlags: () => setPendingGen(null),
  }), [navigateWithPending]);

  const handleSaveAndRegenerateAll = useCallback(() => {
    if (!features.enableAudio) return handleGeneratePdfOnly();
    return navigateWithPending('both', {
      requireMxl: true,
      includeBpm: true,
      startFlags: () => setPendingGen('both'),
      endFlags: () => setPendingGen(null),
    });
  }, [navigateWithPending, handleGeneratePdfOnly]);

  const handleTuningChangeRegenerate = useCallback((scordatura: string) => {
    setPendingScordatura(scordatura);
    setTuningChangeOpen(true);
  }, []);

  const handleConfirmDiscard = useCallback(async () => {
    if (!fingeringData || !originalPdfBlob) {
      showToast('元PDFデータがありません');
      setTuningChangeOpen(false);
      return;
    }
    setTuningChangeOpen(false);
    try {
      // issue #152: 再 OMR (from_pdf_reassign) 経路も通常保存と同じ元タイトル起点で命名する。
      const { originalTitle, fileName } = resolveNaming(fingeringData, {
        storedOriginalTitle, storedFileName, storedMode,
      });
      const fingeringJson = buildExportJson(badges, fingeringData);
      await saveFingeringResult({
        pdfBlob: originalPdfBlob,
        fileName,
        originalTitle,
        fingeringJson,
        originalPdfBlob,
        mxlBlob: mxlBlob ?? undefined,
        detectedBpm,
        mode: storedMode,
      });
      const part = (fingeringData.meta.part as FingeringPart) || 'Guitar1';
      const state: FingeringNavigateState = {
        pendingAction: 'from_pdf_reassign',
        requestedAt: Date.now(),
        scordatura: pendingScordatura,
        part,
        // issue #136: チューニング変更再生成でも初回生成条件 (総譜/パート譜) を引き継ぐ。
        // stored.mode 欠落 (古い stored) は 'part' フォールバック。
        mode: storedMode ?? 'part',
        currentBpm,
        colorMode: pdfColorMode,
      };
      submitOrGate(state);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存に失敗しました');
    }
  }, [fingeringData, originalPdfBlob, badges, mxlBlob, detectedBpm, pendingScordatura, currentBpm, pdfColorMode, storedMode, storedOriginalTitle, storedFileName, submitOrGate, showToast]);

  const handleCancelDiscard = useCallback(() => {
    setTuningChangeOpen(false);
  }, []);

  const handleToggleAddMode = useCallback(() => {
    // issue #170: 追加を ON にするとき (OFF→ON) は move モードを抜けて edit へ矯正する
    //   (範囲選択も clearSelection で解除)。 ON→OFF (配置モード解除) では editorMode を触らない。
    setAddMode((m) => { if (!m) { discardEmptyAddedBadge(); clearSelection(); setPanelOpen(false); setEditorMode('edit'); } return !m; });
  }, [clearSelection, discardEmptyAddedBadge]);

  const handleToolbarZoom = useCallback((newZoom: number) => {
    // toolbar にはカーソル位置がないので container 中央を cursor とみなす
    const container = scrollContainerRef.current;
    if (!container) {
      applyZoomWithPivot(0, 0, newZoom);
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const cx = (containerRect.left + containerRect.right) / 2;
    const cy = (containerRect.top + containerRect.bottom) / 2;
    applyZoomWithPivot(cx, cy, newZoom);
  }, [applyZoomWithPivot]);

  return {
    data: {
      badges, setBadges,
      fingeringData, setFingeringData,
      originalPdfBlob, setOriginalPdfBlob,
      mxlBlob, setMxlBlob,
      badgesHistory, setBadgesHistory,
      pageBadges, chordGroups, measureLines, stats, selected, canUndo,
      highlightedBadgeIds,
    },
    view: {
      zoom, setZoom,
      pan, setPan,
      zoomOrigin, setZoomOrigin,
      isMobile, setIsMobile,
      currentPage, setCurrentPage,
      pageCount, pdfDataUrl,
      toast, setToast,
      loading, setLoading,
    },
    mode: {
      editorMode, setEditorMode,
      addMode, setAddMode,
      // issue #91 round2 (#44): EditorHeader の part 表示出し分け用に storedMode を公開する。
      storedMode,
      // issue #16 Phase 3: selectedIds (ReadonlySet) 系
      selectedIds,
      primarySelectedId,
      selectedCount,
      isMultiSelect,
      selectMode,
      selectRectVisible,
      setSelectRectVisible,
      // 後方互換 (Phase 8 で page 更新後に削除予定)
      selectedId: primarySelectedId,
      setSelectedId,
      panelOpen, setPanelOpen,
    },
    drag: {
      dragBadgeId, setDragBadgeId,
      dragDelta, setDragDelta,
    },
    edit: {
      chordPickerBeatIndex, setChordPickerBeatIndex,
      chordPickerPos, setChordPickerPos,
      detectedBpm, setDetectedBpm,
      currentBpm, setCurrentBpm,
      tuningChangeOpen, setTuningChangeOpen,
      pendingScordatura, setPendingScordatura,
      pdfColorMode, setPdfColorMode,
      repeatEditConfirm,
    },
    regenerate: {
      pendingGen, setPendingGen,
      isGenerating, isGeneratingAudio,
      reauthModalOpen,
      handleReauthConfirm,
      handleReauthCancel,
    },
    refs: {
      svgRef, svgWrapperRef, scrollContainerRef,
      dragRef,
      nextIdRef, nextBeatIndexRef,
      clickSuppressRef,
      zoomRef, panRef, zoomOriginRef, editorModeRef, addModeRef,
    },
    handlers: {
      showToast, pushHistory, handleUndo,
      resetZoomToInitial, applyPanClamp, applyZoomWithPivot,
      clientToSvg,
      dismissPanel, discardEmptyAddedBadge, handlePageChange,
      handleSvgClick, handleBadgeSelect, finalizeDrag,
      addNoteToBeat, handleChordNoteSelect, handleChordAddNote, handleChordPickerClose,
      handleSave, handleDelete,
      // issue #166: 繰り返し編集の確認モーダル分岐
      confirmRepeatIndividual, confirmRepeatAll,
      // issue #16 Phase 3: 範囲削除 + 選択 API
      handleDeleteMany,
      select, toggle, selectRange, clearSelection,
      commitRangeSelectFromVisible,
      handleSaveAndRegenerateAll, handleGeneratePdfOnly, handleGenerateAudioOnly,
      handleTuningChangeRegenerate, handleConfirmDiscard, handleCancelDiscard,
      handleToggleAddMode, handleToolbarZoom,
      tryStartDrag, tryAdvanceDrag,
    },
  };
}
